import type { CustomerEvidenceAggregate, EvidenceCount } from "./customer-evidence";

export type EvidenceMatchType = "common_profile" | "adjacent" | "expansion";
export interface EvidenceDistribution {
  denominator: number;
  basis: string;
  missing: number | null;
  values: (EvidenceCount & { id: string })[];
}
export interface PersonaEvidenceSource {
  evidenceId: string;
  evidenceLabel: string;
  evidenceImportedAt: string;
  aggregate: CustomerEvidenceAggregate;
}
// A later profile step can read older active evidence after the newest import
// was archived. Keep the newest source actually captured across those steps.
export function newestCapturedPersonaEvidence(
  ...sources: (PersonaEvidenceSource | null | undefined)[]
): PersonaEvidenceSource | null {
  return sources.filter((s): s is PersonaEvidenceSource => !!s).sort((a, b) =>
    Date.parse(b.evidenceImportedAt) - Date.parse(a.evidenceImportedAt) ||
    b.evidenceId.localeCompare(a.evidenceId))[0] ?? null;
}
export type EvidenceRefKind = "bucket" | "seniority" | "size" | "industry";
export interface EvidenceRefSelection {
  id: string;
  label: string;
  importId: string;
  importLabel: string;
  count: number;
  pct: number;
  denominator: number;
  companies?: number;
}
export type EvidenceSnapshotIdMap = Record<string, EvidenceRefSelection & { kind: EvidenceRefKind }>;
export interface PersonaEvidenceGenerationCapture {
  version: 1;
  source: PersonaEvidenceSource | null;
  identityStatement?: string;
  idMap?: EvidenceSnapshotIdMap;
  sources?: PersonaEvidenceSource[];
}
export interface EvidenceGenerationResolution {
  status: "valid" | "partial" | "expansion" | "none" | "missing";
  basis: "evidence" | "expansion" | "none";
  bucket: EvidenceRefSelection | null;
  seniority: EvidenceRefSelection | null;
  companySize: EvidenceRefSelection | null;
  industry: EvidenceRefSelection | null;
  droppedIds: string[];
}
export interface EvidenceGenerationRecord extends EvidenceGenerationResolution {
  recordedAt: string;
  source: "generator";
  distributions: EvidenceDistributions;
  sources: { importId: string; importLabel: string; importedAt: string }[];
  caveats: string[];
}
export interface EvidenceDistributions {
  version: 1;
  titles: EvidenceDistribution;
  seniority: EvidenceDistribution;
  companySize: EvidenceDistribution;
  industry: EvidenceDistribution;
}
export interface PersonaEvidencePostMatch {
  evidenceId: string;
  evidenceLabel: string;
  evidenceImportedAt: string;
  matchedAt: string;
  matchType: EvidenceMatchType;
  bucket: EvidenceCount | null;
  seniority: EvidenceCount | null;
  companySize: EvidenceCount | null;
  industry: EvidenceCount | null;
  reason: string;
  caveats: string[];
  basis: "ai_match";
  distributions: EvidenceDistributions;
}
export interface PersonaEvidenceLink extends Partial<PersonaEvidencePostMatch> {
  generation?: EvidenceGenerationRecord;
  agreement?: "agree" | "differ" | "unknown";
}
export function hasPersonaEvidenceMatch(link: PersonaEvidenceLink | null | undefined): link is PersonaEvidenceLink & PersonaEvidencePostMatch {
  return !!link?.evidenceId && ["common_profile", "adjacent", "expansion"].includes(link.matchType ?? "");
}
export function mergePersonaEvidenceLink(
  previous: PersonaEvidenceLink | null | undefined,
  update: { generation?: EvidenceGenerationRecord; postMatch?: PersonaEvidencePostMatch },
): PersonaEvidenceLink {
  // Generation is a first-result record, not an editable model explanation.
  const generation = previous?.generation ?? update.generation;
  const link = { ...previous, ...update.postMatch, ...(generation ? { generation } : {}) };
  if (!generation) return link;
  let agreement: PersonaEvidenceLink["agreement"] = "unknown";
  if (hasPersonaEvidenceMatch(link) && generation.status !== "missing") {
    // Older post-matches have no option ID. Import identity and exact label
    // identify the same captured bucket without comparing legacy index IDs.
    const sameBucket = generation.bucket && link.bucket &&
      generation.bucket.importId === link.evidenceId && generation.bucket.label === link.bucket.label;
    const bothOutside = generation.basis !== "evidence" && link.matchType === "expansion";
    agreement = sameBucket || bothOutside ? "agree" : "differ";
  }
  return { ...link, agreement };
}
export const EVIDENCE_MATCH_REASONS: Record<EvidenceMatchType, string> = {
  common_profile: "The role and work context align with a frequently observed buyer group.",
  adjacent: "The role has some overlap with an observed buyer group but the fit is limited.",
  expansion: "The role does not closely align with the observed buyer groups.",
};
export function safeMatchReason(value: unknown, type: EvidenceMatchType): string {
  // Model explanations are untrusted. Numeric claims and unsupported certainty
  // must not become an observed finding.
  return typeof value === "string" && value.length >= 12 && value.length <= 240 &&
    /^[A-Za-z ,'.()\-]+[.!?]$/.test(value) &&
    !/\b(?:ideal|core|proven|validated)\b/i.test(value)
    ? value : EVIDENCE_MATCH_REASONS[type];
}
